import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDOPListing, printDOP, getBearerToken } from '../services/dopService';
import './ManageDOP.css';

const ManageDOP = () => {
  const pageSize = 10;
  const [dopList, setDopList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('ModifiedOn');
  const [sortDirection, setSortDirection] = useState(1); // 1 for ASC, -1 for DESC
  const navigate = useNavigate();

  // Columns configuration
  const columns = [
    { key: 'Print', label: 'Print', width: '60px', isIcon: true },
    { key: 'Edit', label: 'Edit', width: '60px', isIcon: true },
    { key: 'ResvNo', label: 'Reservation No', width: '120px' },
    { key: 'HandOverDate', label: 'Hand Over Date', width: '120px' },
    { key: 'PropertyNo', label: 'Property No', width: '100px' },
    { key: 'PropertyName', label: 'Property Name', width: '150px' },
    { key: 'UnitNo', label: 'Unit No', width: '100px' },
    { key: 'EWA_AccountNo', label: 'EWA Account No', width: '130px' },
    { key: 'CarParkingNo', label: 'Car Parking No', width: '130px' },
    { key: 'MaintenanceFee', label: 'Maintenance Fee', width: '120px' },
    { key: 'Status', label: 'Status', width: '100px' },
    { key: 'ModifiedBy', label: 'Modified By', width: '100px' },
    { key: 'ModifiedOn', label: 'Modified On', width: '120px' },
  ];

  // Normalize API item to table columns
  const normalizeItem = (it) => {
    if (!it) return {};
    return {
      Id: it.recordID || it.Id || it.id || it.RecordID || null,
      ResvNo: it.resvNo || it.Resv_no || it.ResvNo || it.reservationNo || '',
      HandOverDate: it.handOverDate || it.HandOverDate || it.handOver_Date || it.maintenanceFee || '',
      PropertyNo: it.propertyNo || it.Property_No || it.PropertyNo || '',
      PropertyName: it.propertyName || it.Property_Name || it.PropertyName || '',
      UnitNo: it.unitNo || it.UnitNo || it.unit_no || '',
      EWA_AccountNo: it.ewA_AccountNo || it.EWA_AccountNo || it.ewaAccountNo || '',
      CarParkingNo: it.carParkingNo || it.CarParkingNo || it.car_parking_no || '',
      MaintenanceFee: it.maintenanceFee || it.MaintenanceFee || '',
      Status: it.status || it.Status || '',
      ModifiedBy: it.modifiedBy || it.ModifiedBy || it.modified_by || '',
      ModifiedOn: it.modifiedOn || it.ModifiedOn || it.modifiedon || '',
      // keep original for debugging
      __raw: it,
    };
  };

  /**
   * Fetch DOP listing data
   */
  const fetchDOPList = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getBearerToken();
      if (!token) {
        setError('Authorization token not found. Please log in.');
        return;
      }

      const skip = (currentPage - 1) * pageSize;
      const params = {
        take: pageSize,
        skip: skip,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        search: searchTerm,
      };

      const response = await getDOPListing(token, params);

      // Normalize response to an array of items. API may return different shapes:
      // - an array directly
      // - { data: [...] }
      // - { data: { data: [...] } }
      // - { items: [...] }
      let items = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (Array.isArray(response.data)) {
        items = response.data.items;
      } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      } else if (Array.isArray(response.items)) {
        items = response.items;
      }

      const normalized = (items || []).map(normalizeItem);
      setDopList(normalized);
      setTotalRecords(
        (response && (response.totalRecords || response.total)) ||
          (response && response.data && (response.data.totalRecords || response.data.total)) ||
          normalized.length
      );
    } catch (err) {
      setError(`Failed to fetch DOP records: ${err.message}`);
      console.error('Error fetching DOP list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDOPList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortColumn, sortDirection, searchTerm]);

  /**
   * Handle print action
   */
  const handlePrint = async (dopId) => {
    try {
      const token = getBearerToken();
      if (!token) {
        alert('Authorization token not found. Please log in.');
        return;
      }

      const blob = await printDOP(token, dopId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DOP_${dopId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error printing DOP:', err);
      alert(`Failed to print DOP: ${err.message}`);
    }
  };

  /**
   * Handle edit action
   */
  const handleEdit = (dopId) => {
    navigate(`/dop-form/${dopId}`);
  };

  /**
   * Handle add new DOP
   */
  const handleAddNew = () => {
    navigate('/dop-form');
  };

  /**
   * Handle search
   */
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  /**
   * Handle sort column change
   */
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortColumn(column);
      setSortDirection(1);
    }
    setCurrentPage(1);
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Get icon color based on permissions
   */
  const getIconColor = (hasPermission) => {
    return hasPermission ? '#007bff' : '#cccccc';
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <h1>
          Delivery of Possession (DOP)
          <small>Manage DOP Records</small>
        </h1>
        <ol className="breadcrumb">
          <li><a href="#"><i className="fa fa-dashboard"></i>Home</a></li>
          <li><a href="#">Sales</a></li>
          <li className="active">Delivery of Possession</li>
        </ol>
      </section>

      <section className="content">
        {/* Action Bar */}
        <div className="box box-primary">
          <div className="box-body">
            <div className="row">
              <div className="col-md-6">
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Search</label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search DOP records..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Actions</label>
                <button
                  className="btn btn-primary"
                  onClick={handleAddNew}
                  style={{ marginRight: '10px' }}
                >
                  <i className="fa fa-plus"></i> Add New DOP
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={fetchDOPList}
                  disabled={loading}
                >
                  <i className="fa fa-refresh"></i> Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible">
            <button type="button" className="close" data-dismiss="alert">
              <span>&times;</span>
            </button>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Data Table */}
        <div className="box box-primary">
          <div className="box-header with-border">
            <h3 className="box-title">DOP Records ({totalRecords})</h3>
          </div>
          <div className="box-body">
            <div className="table-responsive">
              <table className="table table-hover table-bordered">
                <thead>
                  <tr style={{ backgroundColor: '#507CD1', color: 'white' }}>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        style={{ width: col.width, cursor: 'pointer' }}
                        onClick={() => !col.isIcon && handleSort(col.key)}
                      >
                        {col.label}
                        {!col.isIcon && sortColumn === col.key && (
                          <i
                            className={`fa ${
                              sortDirection === 1
                                ? 'fa-arrow-up'
                                : 'fa-arrow-down'
                            }`}
                            style={{ marginLeft: '5px' }}
                          ></i>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                        <i className="fa fa-spinner fa-spin"></i> Loading...
                      </td>
                    </tr>
                  ) : dopList.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    dopList.map((item, index) => (
                      <tr key={index} style={{ backgroundColor: '#EFF3FB' }}>
                        {columns.map((col) => {
                          if (col.key === 'Print') {
                            const isCompleted = item.Status === 'Completed';
                            return (
                              <td key={col.key} style={{ textAlign: 'center' }}>
                                <button
                                  className="btn btn-sm btn-link"
                                  onClick={() => isCompleted && handlePrint(item.Id || item.id)}
                                  title={isCompleted ? "Print DOP" : "Print not available (Status must be Completed)"}
                                  style={{
                                    color: isCompleted ? '#007bff' : '#cccccc',
                                    border: 'none',
                                    padding: '0',
                                    cursor: isCompleted ? 'pointer' : 'default',
                                  }}
                                  disabled={!isCompleted}
                                >
                                  <i className="fa fa-print fa-lg"></i>
                                </button>
                              </td>
                            );
                          } else if (col.key === 'Edit') {
                            return (
                              <td key={col.key} style={{ textAlign: 'center' }}>
                                <button
                                  className="btn btn-sm btn-link"
                                  onClick={() => handleEdit(item.Id || item.id)}
                                  title="Edit DOP"
                                  style={{
                                    color: getIconColor(true),
                                    border: 'none',
                                    padding: '0',
                                  }}
                                >
                                  <i className="fa fa-edit fa-lg"></i>
                                </button>
                              </td>
                            );
                          } else if (col.key === 'HandOverDate' || col.key === 'ModifiedOn') {
                            return (
                              <td key={col.key}>
                                {formatDate(item[col.key])}
                              </td>
                            );
                          } else {
                            return (
                              <td key={col.key}>
                                {item[col.key] || '-'}
                              </td>
                            );
                          }
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {!loading && dopList.length > 0 && (
            <div className="box-footer">
              <div className="row">
                <div className="col-sm-7">
                  <div className="dataTables_info">
                    Showing {totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{' '}
                    {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
                  </div>
                </div>
                <div className="col-sm-5">
                  <div className="dataTables_paginate">
                    <ul className="pagination">
                      <li
                        className={currentPage === 1 ? 'disabled' : ''}
                        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                      >
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          «
                        </a>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((pageNum) => {
                          // Show current page and 2 pages before and after
                          return pageNum >= currentPage - 2 && pageNum <= currentPage + 2 && pageNum > 0;
                        })
                        .map((pageNum) => (
                          <li
                            key={pageNum}
                            className={currentPage === pageNum ? 'active' : ''}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            <a href="#" onClick={(e) => e.preventDefault()}>
                              {pageNum}
                            </a>
                          </li>
                        ))}
                      <li
                        className={currentPage === totalPages ? 'disabled' : ''}
                        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                      >
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          »
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ManageDOP;
